import { useCallback, useRef } from 'react';

/**
 * Radix restores focus on close only to a DialogTrigger. Our dialogs are opened from row buttons without
 * one, so focus would fall to <body>. Call `captureOpener` from `onOpenAutoFocus` (before the dialog moves
 * focus, document.activeElement is still the opener) and pass `restoreFocus` as `onCloseAutoFocus`.
 * The ref is only written from event handlers. Own the hook in a component that outlives DialogContent.
 */
export function useDialogFocusRestore() {
  const openerRef = useRef<HTMLElement | null>(null);

  const captureOpener = useCallback(() => {
    const active = document.activeElement;
    openerRef.current = active instanceof HTMLElement && active !== document.body ? active : null;
  }, []);

  const restoreFocus = useCallback((event: Event) => {
    const opener = openerRef.current;
    openerRef.current = null;
    // Opener gone (deleted row) or never captured (Safari/Firefox on macOS don't focus buttons on click):
    // fall back to the list region. If nothing can be focused, let Radix do its default.
    const target = opener && opener.isConnected ? opener : document.querySelector<HTMLElement>('[data-focus-fallback]');
    if (target) {
      event.preventDefault();
      target.focus({ preventScroll: true });
    }
  }, []);

  return { captureOpener, restoreFocus };
}
